<template>
  <div class="lab-container">
    <select
      v-model="tempSelectedLab"
      @change="handleLabSelection"
      class="lab-select"
    >
      <option :value="null" disabled selected>Select a Location</option>
      <option v-for="lab in labs" :value="lab.id" :key="lab.id">
        {{ lab.name }}
      </option>
    </select>

    <!-- Loading Skeleton -->
    <div v-if="isLoading" class="lab-info lab-info-skeleton">
      <div class="skeleton-header"></div>
      <div class="skeleton-text skeleton-text-long"></div>
      <div class="skeleton-text skeleton-text-short"></div>
    </div>

    <!-- Lab Info with Background Image -->
    <div
      v-if="labInfo && !isLoading"
      :key="labInfo.id"
      class="lab-info"
      :style="backgroundImageStyle"
    >
      <div class="background-overlay"></div>
      <div class="lab-content">
        <h2 class="lab-title">{{ labInfo.name }}</h2>
        <div class="lab-details">
          <div class="lab-description">
            <p>{{ labInfo.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/plugins/apiService";

export default {
  props: ["selectedProblem"],
  data() {
    return {
      selectedLab: null,
      tempSelectedLab: null,
      labs: [],
      labInfo: null,
      isLoading: false,
    };
  },
  created() {
    this.fetchLabs();
  },
  mounted() {
    // Emit initial lab info when first lab is loaded
    if (this.selectedLab && this.labs.length > 0) {
      const labName =
        this.labs.find((lab) => lab.id === this.selectedLab)?.name || "";
      this.$emit("selectedLab", { id: this.selectedLab, name: labName });
    }

    // Set the default selected lab to the problem.context_id if available
    if (this.selectedProblem) {
      this.selectedLab = this.selectedProblem;
      this.tempSelectedLab = this.selectedProblem;
      this.fetchLabData();
    }
  },
  computed: {
    backgroundImageStyle() {
      if (this.labInfo && this.labInfo.image) {
        return {
          backgroundImage: `url(${this.labInfo.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
      }
      return {};
    },
  },
  watch: {
    selectedProblem(newVal, oldVal) {
      if (newVal !== oldVal) this.fetchLabs();
    },
  },
  methods: {
    async fetchLabs() {
      if (this.selectedProblem) {
        try {
          const response = await apiClient.get(`/labs`, {
            params: {
              selectedProblem: this.selectedProblem,
            },
          });
          this.labs = response.data;
          if (this.labs.length > 0) {
            this.selectedLab = this.labs[0].id; // Select the first lab by default
            this.fetchLabData(); // Load default lab data
          }
        } catch (error) {
          console.error("Error fetching labs:", error);
        }
      }
    },
    async fetchLabData() {
      if (this.selectedLab) {
        try {
          this.isLoading = true;
          this.labInfo = null;
          const response = await apiClient.post(`/lab_data`, {
            selectedProblem: this.selectedProblem,
            selectedLab: this.selectedLab,
          });
          // Simulate minimum loading time for smooth animation
          await new Promise((resolve) => setTimeout(resolve, 300));
          this.labInfo = response.data[0]; // Get the first element from the lab_info list
          this.isLoading = false;
        } catch (error) {
          console.error("Error fetching lab data:", error);
          this.isLoading = false;
        }
      }
    },
    async handleLabSelection() {
      this.selectedLab = this.tempSelectedLab;
      await this.updateProblemContext();
      this.fetchLabData();
    },
    async updateProblemContext() {
      try {
        const response = await apiClient.post(`/update_problem_location`, {
          problem_id: this.selectedProblem,
          context_id: this.selectedLab,
        });
        // Emit both lab ID and name
        const labName =
          this.labs.find((lab) => lab.id === this.selectedLab)?.name || "";
        this.$emit("selectedLab", { id: this.selectedLab, name: labName });

        if (response.data.error) {
          throw new Error(response.data.error);
        } else {
          this.fetchLabData(); // Refresh the lab data after updating the context
        }
      } catch (error) {
        console.error("Error updating problem context:", error);
      }
    },
  },
};
</script>

<style scoped>
/* Container */
.lab-container {
  padding: 24px;
  border-radius: 12px;
  flex: 1;
  min-width: 400px;
}

/* Select */
.lab-select {
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  border-radius: 8px;
  border: 2px solid var(--secondaryColor);
  margin-bottom: 20px;
  color: var(--darkColor);
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.lab-select:hover {
  border-color: var(--primaryColor);
}

.lab-select:focus {
  outline: none;
  border-color: var(--primaryColor);
  box-shadow: 0 0 0 3px rgba(95, 151, 122, 0.2);
}

/* Lab Info with Background Image */
.lab-info {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  min-height: 300px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 30px;
}

/* Background Overlay */
.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    170deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(255, 255, 255, 0.7) 70%,
    rgba(255, 255, 255, 0.5) 100%
  );
  z-index: 1;
}

/* Content on top of background */
.lab-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.lab-title {
  width: 100%;
  margin-bottom: 20px;
  color: var(--primaryColor);
  font-size: 24px;
  font-weight: 700;
}

.lab-details {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.lab-description {
  flex: 1;
  text-align: left;
}

.lab-description p {
  color: var(--darkColor);
  line-height: 1.7;
  font-size: 14px;
  margin: 0;
}

/* Loading Skeleton */
.lab-info-skeleton {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  box-shadow: none;
}

.skeleton-header {
  height: 28px;
  background: linear-gradient(
    90deg,
    var(--secondaryColorLight) 25%,
    var(--secondaryColor) 50%,
    var(--secondaryColorLight) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
  border-radius: 6px;
  margin-bottom: 16px;
  width: 60%;
}

.skeleton-text {
  height: 12px;
  background: linear-gradient(
    90deg,
    var(--secondaryColorLight) 25%,
    var(--secondaryColor) 50%,
    var(--secondaryColorLight) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
}

.skeleton-text-long {
  width: 100%;
}

.skeleton-text-short {
  width: 80%;
}

/* Animations */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.95;
  }
}

/* Transitions */
.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.fade-in-enter-from {
  opacity: 0;
  transform: scale(0.98);
}

.fade-in-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.fade-in-enter-to,
.fade-in-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
